'use client';

import { useEffect, useRef } from 'react';
import type HlsType from 'hls.js';
import type PlyrLib from 'plyr';
import { parseVideoRef } from '@cfixe-monorepo/shared';
import 'plyr/dist/plyr.css';

const HLS_BASE = 'https://vod.api.video/vod';

// Plyr controls list — 'download' is intentionally excluded.
const PLYR_CONTROLS: string[] = [
  'play-large',
  'play',
  'progress',
  'current-time',
  'mute',
  'volume',
  'captions',
  'settings',
  'pip',
  'airplay',
  'fullscreen',
];

export type VideoOrientation = 'landscape' | 'portrait';

interface Props {
  videoRef: string;
  title?: string;
  /**
   * Forces the player aspect ratio. When omitted, YouTube Shorts auto-default to
   * portrait and everything else keeps the provider's native ratio.
   */
  orientation?: VideoOrientation;
}

export default function VideoPlayer({ videoRef, title, orientation }: Props): React.ReactElement | null {
  const mountRef = useRef<HTMLDivElement>(null);
  const parsed = parseVideoRef(videoRef);
  const isPortrait = orientation === 'portrait' || (orientation == null && parsed.isShort === true);

  useEffect((): (() => void) | undefined => {
    const mount = mountRef.current;
    if (!mount) return undefined;
    if (parsed.kind === 'invalid') return undefined;

    let plyrInstance: PlyrLib | null = null;
    let hlsInstance: HlsType | null = null;
    let cancelled = false;

    const load = async (): Promise<void> => {
      const [{ default: PlyrCtor }, { default: HlsCtor }] = await Promise.all([import('plyr'), import('hls.js')]);
      if (cancelled || !mountRef.current) return;

      const plyrOptions: PlyrLib.Options = {
        controls: PLYR_CONTROLS,
        settings: ['captions', 'quality', 'speed'],
        youtube: { noCookie: true, rel: 0, modestbranding: 1 },
        // Only force a ratio for portrait; landscape keeps the provider's native ratio.
        ...(isPortrait ? { ratio: '9:16' } : {}),
      };

      let targetElement: HTMLElement;

      if (parsed.kind === 'youtube') {
        const div = document.createElement('div');
        div.setAttribute('data-plyr-provider', 'youtube');
        div.setAttribute('data-plyr-embed-id', parsed.id);
        mount.appendChild(div);
        targetElement = div;
      } else {
        const videoEl = document.createElement('video');
        videoEl.setAttribute('playsinline', '');
        videoEl.setAttribute('crossorigin', 'anonymous');
        if (title) videoEl.setAttribute('title', title);
        mount.appendChild(videoEl);

        const hlsUrl = `${HLS_BASE}/${parsed.id}/hls/manifest.m3u8`;
        if (HlsCtor.isSupported()) {
          hlsInstance = new HlsCtor();
          hlsInstance.loadSource(hlsUrl);
          hlsInstance.attachMedia(videoEl);
        } else if (videoEl.canPlayType('application/vnd.apple.mpegurl')) {
          videoEl.src = hlsUrl;
        }
        targetElement = videoEl;
      }

      plyrInstance = new PlyrCtor(targetElement, plyrOptions);
    };

    void load();

    return (): void => {
      cancelled = true;
      plyrInstance?.destroy();
      hlsInstance?.destroy();
      while (mount.firstChild) mount.removeChild(mount.firstChild);
    };
  }, [videoRef, title, isPortrait]);

  return (
    <div ref={mountRef} className="vod-player__plyr-wrapper" data-orientation={isPortrait ? 'portrait' : 'landscape'} />
  );
}
